import { easeBoth } from '@utils/anim.ts';

import { fitPitchedBounds } from './fitPitchedBounds.ts';
import type { Bounds, MapControllerConfig, MapInitialized, MapRendererApp, Padding } from './types.ts';
import { convertRadiusToZoom } from './utils.ts';

type ValidMapPadding = Exclude<Padding, 'mol'> | number;

type FlyAnimOptions = {
  padding?: ValidMapPadding;
  speed?: number;
  mapAction?: string;
  [key: string]: unknown;
};

type ViewAnimParams = {
  padding?: ValidMapPadding;
  duration?: number;
  [key: string]: unknown;
};

type FitBoundsAnimOptions = {
  padding?: ValidMapPadding;
  bearing?: number;
  pitch?: number;
  speed?: number;
  [key: string]: unknown;
};

export default function AnimationController(
  app: MapRendererApp,
  mapInitialized: MapInitialized,
  config: MapControllerConfig,
) {
  let appPadding: Exclude<Padding, 'mol'> = { bottom: 0, top: 0, right: 0, left: 0 }; // listens for map/changePadding events to alter

  const DEFAULT_FLYTO_SPEED = 0.9; // default speed for map animations employing the flyTo method. Expressed in screens per second
  const DEFAULT_FITBOUNDS_SPEED = 1.2; // default speed for map animations employing the fitBounds method. Expressed in screens per second
  const DEFAULT_EASETO_DURATION = 1000; // default speed for map animations employing the easeTo method. Expressed in ms
  const DEFAULT_LINEAR_DURATION = 500; // default speed for map animations employing the panBy method. Expressed in ms
  const CLAMPED_ANCHOR_OFFSET_RATIO = 0.2; // when a centerInBounds minZoom clamp applies, shifts the anchor down by this fraction of the visible (post-padding) map height

  let globalAnimSpeedMultiplier = config.globalAnimSpeedMultiplier || 1.0; // global speed factor for animations, 2 = twice as fast, 0.5 = half as fast
  if (import.meta.env?.MODE === 'test' && config.cypressAnimSpeedMultiplier) {
    globalAnimSpeedMultiplier = config.cypressAnimSpeedMultiplier;
  } // tests can override the speed multiplier via cypressAnimSpeedMultiplier config

  app.bus.on(
    'map/animateToPointWithRadius',
    ({
      lat,
      lng,
      floorId = null,
      radius,
      relSpeed = 1.0,
      animOptions = {},
      ...rest
    }: {
      lat: number;
      lng: number;
      floorId?: string | number | null;
      radius: number;
      relSpeed?: number;
      animOptions?: FlyAnimOptions;
    } & Record<string, unknown>) => {
      mapInitialized().then(async map => {
        if (floorId) {
          app.bus.send('map/changeFloor', { id: floorId });
        }
        const validRadius = Math.max(radius, config.minZoomRadius ?? 0);
        const venueCenter = await app.bus.get<undefined, { lat: number; lng: number }>('venueData/getVenueCenter');
        const convertedZoom = convertRadiusToZoom(validRadius, venueCenter, map, app.config.renderDiv);
        const { mapAction, ...cleanAnimOptions } = animOptions;

        if (!cleanAnimOptions.padding && !rest.padding) {
          rest.padding = appPadding;
        }

        cleanAnimOptions.speed = DEFAULT_FLYTO_SPEED * relSpeed * globalAnimSpeedMultiplier;

        map.flyTo(
          {
            center: [lng, lat],
            zoom: convertedZoom,
            ...cleanAnimOptions,
            ...rest, // allow passing other props, such as padding
          },
          { mapAction },
        );
      });
    },
  );

  const setPadding = (padding: Padding, map: Awaited<ReturnType<MapInitialized>>, keepView = false) => {
    if (padding === 'mol') {
      padding = app.env.isDesktop()
        ? { left: 347, right: 55, top: 72, bottom: 22 }
        : { top: 0, bottom: 0, left: 0, right: 0 };
    }

    if (keepView) {
      // This keeps the map from panning to re-center the map. If keepView is false, the map will pan so the center remains the same relative to the new padding.
      const { clientWidth, clientHeight } = map.getCanvas();
      const { top = 0, bottom = 0, left = 0, right = 0 } = padding;
      const center = map.unproject([(clientWidth + left - right) / 2, (clientHeight + top - bottom) / 2]);
      map.jumpTo({ center, padding });
    } else {
      map.setPadding(padding);
    }
    appPadding = padding;
  };

  app.bus.on('map/changePadding', ({ padding, keepView = false }: { padding: Padding; keepView?: boolean }) =>
    mapInitialized().then(map => setPadding(padding, map, keepView)),
  );

  app.bus.on('map/getPadding', () => appPadding);

  app.bus.on(
    'map/animateToPoint',
    ({
      lat,
      lng,
      floorId = null,
      zoom = null,
      relSpeed = 1.0,
      animOptions = {},
    }: {
      lat: number;
      lng: number;
      floorId?: string | number | null;
      zoom?: number | null;
      relSpeed?: number;
      animOptions?: FlyAnimOptions;
    }) =>
      mapInitialized().then(map => {
        if (floorId) {
          app.bus.send('map/changeFloor', { id: floorId });
        }
        const validZoom = zoom !== null ? zoom : map.getZoom();
        const { mapAction, ...cleanAnimOptions } = animOptions;

        if (!cleanAnimOptions.padding) {
          cleanAnimOptions.padding = appPadding;
        }

        cleanAnimOptions.speed = DEFAULT_FLYTO_SPEED * relSpeed * globalAnimSpeedMultiplier;

        map.flyTo({ center: [lng, lat], zoom: validZoom, ...cleanAnimOptions }, { mapAction });
      }),
  );

  app.bus.on(
    'map/animateToView',
    ({ relSpeed = 1.0, mapAction, ...params }: { relSpeed?: number; mapAction?: string } & ViewAnimParams) =>
      mapInitialized().then(map => {
        if (!params.padding) {
          params.padding = appPadding;
        }

        params.duration = DEFAULT_EASETO_DURATION * (1 / relSpeed) * (1 / globalAnimSpeedMultiplier);

        map.easeTo({ easing: easeBoth, ...params }, { mapAction });
      }),
  );

  app.bus.on(
    'map/pan',
    ({ offset, relSpeed, mapAction = 'pan' }: { offset: [number, number]; relSpeed: number; mapAction?: string }) =>
      mapInitialized().then(map => {
        const options = {
          duration: DEFAULT_LINEAR_DURATION * (1 / relSpeed) * (1 / globalAnimSpeedMultiplier),
        };
        map.panBy(offset, options, { mapAction });
      }),
  );

  app.bus.on(
    'map/centerInBounds',
    async ({
      bounds,
      coordinates,
      bearing = null,
      floorId = null,
      pitch = null,
      relSpeed = 1.0,
      animOptions = {},
      clamp,
    }: {
      bounds: Bounds;
      coordinates?: [number, number][];
      bearing?: number | null;
      floorId?: string | number | null;
      pitch?: number | null;
      relSpeed?: number;
      animOptions?: FitBoundsAnimOptions;
      /** Holds zoom at `minZoom` if the bounds would fit below it, keeping `anchor` (e.g. route start) in view. */
      clamp?: { minZoom: number; anchor: { lat: number; lng: number } };
    }) =>
      mapInitialized().then(map => {
        if (floorId) {
          app.bus.send('map/changeFloor', { id: floorId });
        }
        const validBearing = bearing !== null ? bearing : map.getBearing();
        const validPitch = pitch !== null ? pitch : map.getPitch();
        const fbOptions = {
          bearing: validBearing,
          pitch: validPitch,
          ...animOptions,
        };

        fbOptions.speed = DEFAULT_FITBOUNDS_SPEED * relSpeed * globalAnimSpeedMultiplier;

        const lngLatBounds: [[number, number], [number, number]] = [
          [bounds.w, bounds.s],
          [bounds.e, bounds.n],
        ];

        if (clamp) {
          const { minZoom, anchor } = clamp;
          const padding = animOptions.padding;
          const camera = fitPitchedBounds(map, lngLatBounds, fbOptions, coordinates);

          if (camera && camera.zoom !== undefined && camera.zoom < minZoom) {
            // Bounds don't fit at an acceptable zoom: hold at minZoom and keep the anchor in view.
            // The caller aligns bearing with the route direction, so "ahead" is screen-up; shifting the
            // center down by a fraction of the visible area keeps the anchor visible with the route continuing
            // upward. The fraction is of the area left after padding (the offset is applied from the center of
            // that area), so the anchor can't be pushed under whatever the padding is reserving space for.
            const verticalPadding =
              typeof padding === 'number' ? padding * 2 : (padding?.top ?? 0) + (padding?.bottom ?? 0);
            const visibleHeight = Math.max(0, map.getContainer().clientHeight - verticalPadding);
            const offsetY = Math.round(visibleHeight * CLAMPED_ANCHOR_OFFSET_RATIO);
            map.flyTo({
              center: [anchor.lng, anchor.lat],
              zoom: minZoom,
              bearing: validBearing,
              pitch: fbOptions.pitch,
              padding,
              speed: fbOptions.speed,
              offset: [0, offsetY],
            } as Parameters<typeof map.flyTo>[0]);
            return;
          }
          if (camera) {
            // The camera already accounts for UI padding; don't change persistent map padding.
            const { padding: _padding, ...options } = fbOptions;
            map.flyTo({ ...options, ...camera });
            return;
          }
        }

        map.fitBounds(lngLatBounds, fbOptions);
      }),
  );
}
